import React, { useMemo, useRef, useState } from 'react';
import Loader from '../../components/Loader';
import Timeline from '../../components/Timeline/Timeline';
import { ServiceRequestDetailHeader } from 'src/pages/ServiceRequest/components/ServiceRequestDetailHeader';
import ServiceRequestTabs from 'src/pages/ServiceRequest/ServiceRequestTabs';
import ServiceRequestOverlays from 'src/pages/ServiceRequest/ServiceRequestOverlays';
import {
  useServiceRequestPageOrchestrator,
  UseServiceRequestPageOrchestratorReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestPageOrchestrator';
import ErrorDisplay from 'src/components/ErrorDisplay/ErrorDisplay';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';
import 'react-image-lightbox/style.css';
import './ServiceRequest.scss';
import './ServiceRequestDetails/ServiceRequestDetails.scss';

interface ServiceRequestProps {
  match: {
    params: {
      id: string;
    };
  };
  location: {
    state?: {
      activeTab?: number;
    };
  };
  history: any;
}

const ServiceRequest = ({
  match,
  location,
  history,
}: ServiceRequestProps) => {
  const serviceRequestId: string = match.params.id;

  const {
    user,
    serviceRequest,
    activity,
    loading,
    error,
    homeOwnerDataVisibility,
    showEditOverlay,
    showAssignOverlay,
    showScheduleOverlay,
    showDeclinePopup,
    openEditOverlay,
    closeEditOverlay,
    closeAssignOverlay,
    closeScheduleOverlay,
    refreshData,
    fetchServiceRequestById,
    handleDeleteServiceRequest,
    handleStatusChange,
    handleStatusAssign,
    onSubmitDeclinePopup,
    handleEditFormSaved,
    handleSaveScheduledAppointment,
    downloadPdf,
    subscribers,
    subscribersLoading,
    subscribersSaving,
    saveSubscribers,
    isCurrentUserSubscribed,
  }: UseServiceRequestPageOrchestratorReturn = useServiceRequestPageOrchestrator(
    {
      serviceRequestId,
      onAfterDelete: () => history.push('/app/service-requests'),
    }
  );

  const activeTab: number = useMemo(() => {
    if (location?.state?.activeTab !== undefined) {
      return location.state.activeTab;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const tabParam: string = queryParams.get('activeTab');

    return tabParam ? Number(tabParam) : 0;
  }, [location]);

  const loadingSR: boolean = !serviceRequest?.id;

  const pageTitle: string =
    serviceRequest?.ticketNumber ??
    (serviceRequest?.ticketId ? String(serviceRequest.ticketId) : 'Service Request');

  const [pinRefreshKey, setPinRefreshKey] = useState<number>(0);

  const bumpPinRefreshKey = (): void => {
    setPinRefreshKey((previous: number) => previous + 1);
  };

  const [openNotesSignal, setOpenNotesSignal] = useState<number>(0);

  const bottomContentRef = useRef<HTMLDivElement>(null);

  const openNotesTab = (): void => {
    setOpenNotesSignal((previous: number) => previous + 1);

    requestAnimationFrame(() => {
      bottomContentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    });
  };

  if (error) {
    return (
      <ErrorDisplay
        title="Couldn't Load Service Request"
        message="We couldn't load this service request. If the issue persists, please contact support."
        onRetry={() => fetchServiceRequestById(serviceRequestId)}
        buttonText="Try Again"
        colorScheme="red"
      />
    );
  }

  return (
    <>
      {!loadingSR && loading ? <Loader center={true} /> : null}
      <WithNavigation
        title={pageTitle}
        top={
          <ServiceRequestDetailHeader
            serviceRequest={serviceRequest}
            location={location}
            onEditServiceRequest={openEditOverlay}
            onDeleteServiceRequest={handleDeleteServiceRequest}
            onDownloadPdf={() => downloadPdf(serviceRequest?.id)}
            handleStatusChange={handleStatusChange}
            loadingSR={loadingSR}
            homeOwnerDataVisibility={homeOwnerDataVisibility}
            subscribers={subscribers}
            subscribersLoading={subscribersLoading}
            subscribersSaving={subscribersSaving}
            onSaveSubscribers={saveSubscribers}
            isCurrentUserSubscribed={isCurrentUserSubscribed}
            pinRefreshKey={pinRefreshKey}
            onOpenPinnedNote={openNotesTab}
          />
        }
        bottom={
          <div className="serviceRequest" ref={bottomContentRef}>
            <Timeline activity={activity} loading={loading || loadingSR} />
            <ServiceRequestTabs
              user={user}
              requestId={serviceRequestId}
              activeTab={activeTab}
              loadingSR={loadingSR}
              serviceRequest={serviceRequest}
              refreshData={refreshData}
              onNotePinned={bumpPinRefreshKey}
              openNotesSignal={openNotesSignal}
            />
            <ServiceRequestOverlays
              serviceRequest={serviceRequest}
              showEditOverlay={showEditOverlay}
              showAssignOverlay={showAssignOverlay}
              showScheduleOverlay={showScheduleOverlay}
              showDeclinePopup={showDeclinePopup}
              closeEditOverlay={closeEditOverlay}
              closeAssignOverlay={closeAssignOverlay}
              closeScheduleOverlay={closeScheduleOverlay}
              handleEditFormSaved={handleEditFormSaved}
              handleSaveScheduledAppointment={handleSaveScheduledAppointment}
              handleStatusAssign={handleStatusAssign}
              onSubmitDeclinePopup={onSubmitDeclinePopup}
            />
          </div>
        }
      />
    </>
  );
};

export default ServiceRequest;
